// تعیین فعالیت‌های کارکنان — فهرست افراد
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTabs } from '../TabManager';
import ListPage, { ui } from '../../components/ListPage';
import BudgetPeopleActivityDetail from './BudgetPeopleActivityDetail';

const toFa   = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const toEng  = s => String(s ?? '').replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());
const fmtNum = n => n ? Number(n).toLocaleString('fa-IR') : '•';

const API = (path, opts = {}) => fetch(`/api/budget${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(r => r.json());

export default function BudgetPeopleActivity({ user, onBack }) {
  const curYear = new Date().toLocaleDateString('fa-IR').split('/')[0]
    .replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());

  const [year,   setYear]   = useState(curYear);
  const [search, setSearch] = useState('');
  const [data,     setData]     = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const { openTab, goHome } = useTabs();

  const cols = ['کد ملی', 'نام و نام خانوادگی', 'جمع مبلغ محول', 'جمع درصد فعالیت‌ها', 'وضعیت'];
  const initW = [130, 240, 160, 150, 120];
  const [colW, setColW] = useState(initW);
  const colWRef = useRef(initW);
  useEffect(() => { colWRef.current = colW; }, [colW]);
  const startResize = useCallback((idx, e) => {
    e.preventDefault(); e.stopPropagation();
    const sx = e.clientX, sw = colWRef.current[idx];
    const onMove = ev => setColW(w => { const n = [...w]; n[idx] = Math.max(40, sw + (sx - ev.clientX)); return n; });
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
  }, []);

  const load = useCallback(async (yr = year) => {
    setLoading(true); setError(''); setSelected(null);
    const j = await API(`/people-activity/?year=${yr}&search=${encodeURIComponent(search)}`);
    setLoading(false);
    if (j.ok) setData(j.data);
    else setError(j.msg);
  }, [year, search]);

  useEffect(() => { load(); }, []);

  const handleYear = e => {
    const v = toEng(e.target.value.replace(/[^0-9۰-۹]/g, '')).slice(0, 4);
    setYear(v);
    if (v.length === 4) load(v);
  };

  const openDetail = () => {
    if (!selected) return;
    const person = { ...selected, year };
    openTab(`ppl-act-detail:${selected.id}`, `فعالیت‌های ${selected.name}`, '🧑‍💼',
      () => <BudgetPeopleActivityDetail user={user} onBack={goHome} person={person} />);
  };

  const filtered = data.filter(r => !search || r.name.includes(search) || (r.melli_code || '').includes(search));

  const toolbar = (
    <>
      <div style={ui.toolBtns}>
        <button onClick={openDetail} disabled={!selected} style={{ ...ui.btnItems, opacity: selected ? 1 : 0.4, cursor: selected ? 'pointer' : 'default' }}>🧑‍💼 فعالیت‌های فرد</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <label style={ui.flabel}>سال مالی</label>
        <input value={toFa(year)} onChange={handleYear} maxLength={4} placeholder="۱۴۰۵" style={ui.yearInput} />
      </div>
      <input value={search} onChange={e => setSearch(e.target.value)}
        style={{ ...ui.searchInput, flex: 1, minWidth: '150px' }} placeholder="جستجو (نام یا کد ملی)..." />
      {loading && <span>⏳</span>}
      {!loading && filtered.length > 0 && <div style={ui.countBadge}>{toFa(filtered.length)} نفر</div>}
    </>
  );

  return (
    <>
      <ListPage user={user} onBack={onBack} title="تعیین فعالیت‌های کارکنان" subtitle="بخش بودجه — سامانه ارجان"
        toolbar={toolbar} error={error}>
        <table style={{ ...ui.table, tableLayout: 'fixed' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <tr>
              {cols.map((h, i) => (
                <th key={i} style={{ ...ui.th, width: colW[i], minWidth: 40, position: 'relative', userSelect: 'none' }}>
                  {h}<span onMouseDown={e => startResize(i, e)} style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', cursor: 'col-resize' }} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => {
              const sel = selected?.id === r.id;
              const bg  = sel ? '#1a2f50' : r.incomplete ? (i % 2 === 0 ? '#e3f2fd' : '#d6ecfb') : (i % 2 === 0 ? '#fff' : '#fdf6e3');
              const tc  = sel ? '#fff' : '#111';
              return (
                <tr key={r.id} style={{ background: bg, cursor: 'pointer' }}
                  onClick={() => setSelected(sel ? null : r)} onDoubleClick={() => { setSelected(r); setTimeout(openDetail, 0); }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = '#e8f0fe'; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = bg; }}>
                  <td style={{ ...ui.td, fontFamily: 'monospace', color: tc }}>{toFa(r.melli_code) || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', fontWeight: '600', color: tc }}>{r.name || '•'}</td>
                  <td style={{ ...ui.tdNum, color: sel ? '#fff' : '#1b5e20' }}>{fmtNum(r.havaleh_amount)}</td>
                  <td style={{ ...ui.tdNum, color: sel ? '#fff' : (r.incomplete ? '#c62828' : '#1a237e'), fontWeight: '700' }}>{toFa(r.sum_percent)}٪</td>
                  <td style={{ ...ui.td, fontSize: '11px', color: sel ? '#fff' : (r.incomplete ? '#c62828' : '#1b5e20') }}>{r.incomplete ? 'ناقص' : (r.sum_percent === 100 ? 'کامل' : '—')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && filtered.length === 0 && <div style={ui.empty}>📭 کارمندی با تراکنش بودجه در این سال یافت نشد</div>}
      </ListPage>
    </>
  );
}
